import { check } from "../assets";
import { motion } from "framer-motion";
import { benefits } from "../constants";

const cardVariants = {
  initial: { y: 20, opacity: 0 },
  animate: { y: 0, opacity: 1 },
  hover: { 
    y: -5,
    transition: {
      duration: 0.3,
      ease: "easeOut"
    }
  }
};

const Services = () => {
  return (
    <section className="relative w-full py-20">
      {/* Background gradients */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-48 left-0 w-[800px] h-[800px] bg-radial-gradient from-[#1FA2FF]/20 via-transparent to-transparent opacity-50 mix-blend-overlay" />
        <div className="absolute -bottom-48 right-0 w-[800px] h-[800px] bg-radial-gradient from-[#FF61D2]/20 via-transparent to-transparent opacity-50 mix-blend-overlay" />
      </div>

      <div className="container relative z-2 mx-auto px-4">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <div className="relative inline-block">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-500/40 to-transparent blur-xl"
              />
              <h2 className="text-5xl md:text-6xl font-bold mb-4 relative">
                Our <span className="gradient-text">Services</span>
              </h2>
            </div>
            <p className="text-lg text-gray-500 dark:text-gray-400 max-w-3xl mx-auto">
              Leverage the power of artificial intelligence to transform your business with our comprehensive suite of AI services.
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((service, index) => (
            <motion.div
              key={service.id}
              variants={cardVariants}
              initial="initial"
              whileInView="animate"
              whileHover="hover"
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="relative group"
            >              
              <div className="relative h-full">
                <div className="absolute inset-0 bg-gradient-to-r from-[#6366f1] via-[#a855f7] to-[#ec4899] rounded-2xl blur-xl opacity-0 group-hover:opacity-10 transition-opacity duration-500" />
                <div className="relative bg-white/40 dark:bg-[#0A0F1C]/40 p-6 rounded-2xl h-full border border-gray-200 dark:border-[#1E2A4A] group-hover:border-gray-300 dark:group-hover:border-gray-700 transition-colors duration-300">
                  <motion.div
                    initial={{ scale: 1 }}
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.2 }}
                    className="mb-6"
                  >
                    <div className="w-12 h-12 mb-4">
                      <img src={service.iconUrl} alt={service.title} className="w-full h-full" />
                    </div>
                    <h3 className="text-xl font-semibold mb-3 bg-gradient-to-r from-[#6366f1] via-[#a855f7] to-[#ec4899] bg-clip-text text-transparent">
                      {service.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{service.text}</p>
                    
                    {/* Features List */}
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <motion.li
                          key={idx}
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, delay: idx * 0.1 }}
                          viewport={{ once: true }}
                          className="flex items-center gap-2"
                        >
                          <div className="flex-shrink-0 w-4 h-4 rounded-full bg-gradient-to-r from-[#6366f1] via-[#a855f7] to-[#ec4899] flex items-center justify-center p-1">
                            <img src={check} alt="Check" className="w-full h-full" />
                          </div>
                          <span className="text-xs text-gray-600 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                            {feature}
                          </span>
                        </motion.li>
                      ))}
                    </ul>
                  </motion.div>
                  
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.3 }}
                    viewport={{ once: true }}
                    className="mt-4 pt-4 border-t border-gray-200 dark:border-[#1E2A4A] group-hover:border-gray-300 dark:group-hover:border-gray-700 transition-colors"
                  >
                    <button className="w-full text-sm button button-primary py-2">
                      Learn More
                    </button>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <p className="text-gray-500 dark:text-gray-400 mb-4">Need a custom solution?</p>
          <button className="button button-secondary">
            Contact Our Team
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
